import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { StudioPage, Metric, Panel, Badge, Loading, cx } from "./StudioShared";
import {
  DollarSign, Cpu, UserPlus, Trophy, Users, Briefcase, Radio, Globe,
  TrendingUp, LayoutGrid, Server, AlertTriangle, CheckCircle2, Info, Lightbulb,
} from "lucide-react";

const PRESETS = [["today", "Today"], ["week", "This Week"], ["month", "This Month"]];
const KPI_ICON = {
  revenue_today: DollarSign, ai_spend: Cpu, new_leads: UserPlus, win_rate: Trophy,
  active_clients: Users, active_projects: Briefcase, active_missions: Radio,
  website_builds: Globe, pipeline: TrendingUp, top_template: LayoutGrid,
  fastest_provider: Server, best_model: Cpu,
};
const fmt = (m) => {
  if (m.value == null || m.value === "—") return "—";
  if (m.fmt === "usd") return `$${Number(m.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (m.fmt === "pct") return `${m.value}%`;
  if (m.fmt === "ms") return Number(m.value) < 1000 ? `${m.value}ms` : `${(m.value / 1000).toFixed(1)}s`;
  return m.value;
};
const ALERT_ICON = { critical: AlertTriangle, warning: AlertTriangle, info: Info, success: CheckCircle2 };
const ALERT_TONE = {
  critical: "text-red-300 border-red-500/30", warning: "text-amber-300 border-amber-500/30",
  info: "text-slate-300 border-white/10", success: "text-emerald-300 border-emerald-500/30",
};

export default function CommandCenter() {
  const [preset, setPreset] = useState("today");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try { const r = await api.get(`/exec/command-center?preset=${preset}`); setData(r.data); }
    catch { setData({ kpis: [], alerts: [], recommendations: [] }); }
  }, [preset]);
  useEffect(() => { load(); }, [load]);

  return (
    <StudioPage
      title="Executive Command Center"
      question="Everything that matters about your AI-powered business — on one screen."
      testid="command-center"
      actions={
        <select value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="cc-preset"
          className="bg-navy-800 border border-white/10 px-2.5 py-1.5 text-xs text-white rounded-sm focus:border-gold focus:outline-none">
          {PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      }
    >
      {!data ? <Loading label="Assembling command center…" /> : (
        <div className="space-y-6">
          {/* Health banner */}
          <div className={cx("flex items-center gap-2 border rounded-sm px-4 py-2.5",
            data.health === "healthy" ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5")}
            data-testid="cc-health">
            {data.health === "healthy" ? <CheckCircle2 size={16} className="text-emerald-400" /> : <AlertTriangle size={16} className="text-amber-400" />}
            <span className="text-white text-sm font-medium">Platform Health: {data.health === "healthy" ? "Healthy" : "Needs Attention"}</span>
          </div>

          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="cc-kpis">
            {data.kpis.map((m) => (
              <Metric key={m.key} label={m.label} value={fmt(m)} sub={m.sub} icon={KPI_ICON[m.key] || TrendingUp}
                testid={`cc-kpi-${m.key}`} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Alerts */}
            <Panel className="p-4" testid="cc-alerts">
              <div className="flex items-center gap-2 text-white text-sm font-semibold mb-3"><AlertTriangle size={15} className="text-gold" /> System Alerts</div>
              <div className="space-y-2">
                {data.alerts.map((a, i) => {
                  const Icon = ALERT_ICON[a.severity] || Info;
                  return (
                    <div key={i} className={cx("flex items-start gap-2 border rounded-sm px-3 py-2 text-xs", ALERT_TONE[a.severity])} data-testid={`cc-alert-${i}`}>
                      <Icon size={13} className="mt-0.5 shrink-0" /><span>{a.text}</span>
                    </div>
                  );
                })}
              </div>
            </Panel>

            {/* Recommendations */}
            <Panel className="p-4" testid="cc-recommendations">
              <div className="flex items-center gap-2 text-white text-sm font-semibold mb-3"><Lightbulb size={15} className="text-gold" /> AIPOS Recommendations</div>
              <div className="space-y-2">
                {(data.recommendations || []).length === 0 ? <div className="text-slate-500 text-xs">No recommendations yet.</div> :
                  data.recommendations.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 border border-white/10 rounded-sm px-3 py-2 text-xs" data-testid={`cc-rec-${i}`}>
                      <Badge tone={r.type === "ai" ? "gold" : r.type === "business" ? "emerald" : "slate"}>{r.type}</Badge>
                      <span className="text-slate-200">{r.text}</span>
                    </div>
                  ))}
              </div>
            </Panel>
          </div>
        </div>
      )}
    </StudioPage>
  );
}
